import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    Typography
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CustomAxios from "../utils/CustomAxios";

const ACCEPTED_EXTENSIONS = ["jpg", "jpeg", "png", "bmp", "webp", "avif", "tif", "tiff", "heic", "heif"];
const UPLOAD_CHUNK_SIZE = 100;

const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

export const BulkPhotoUpload = () => {
    const fileInputRef = useRef(null);
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [chunkProgress, setChunkProgress] = useState({ current: 0, total: 0 });
    const [apiResult, setApiResult] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedVisibleCount, setSelectedVisibleCount] = useState(50);
    const [uploadSummaries, setUploadSummaries] = useState([]);
    const [summaryVisibleCount, setSummaryVisibleCount] = useState(20);

    useEffect(() => {
        setSelectedVisibleCount(50);
    }, [files.length]);

    useEffect(() => {
        setSummaryVisibleCount(20);
    }, [uploadSummaries.length]);

    const appendSummaryEntry = useCallback((uploadedItems, failedItems) => {
        setUploadSummaries((prev) => [
            ...prev,
            {
                id: `${Date.now()}-${prev.length}`,
                uploadedCount: uploadedItems.length,
                failedCount: failedItems.length,
                status:
                    failedItems.length === 0
                        ? "success"
                        : uploadedItems.length > 0
                            ? "partial"
                            : "error",
                uploaded: uploadedItems,
                failed: failedItems,
                timestamp: new Date().toISOString(),
            },
        ]);
    }, []);

    const enhancedFiles = useMemo(() => {
        return files.map((file) => {
            const [rawId] = file.name.split(".");
            const memberId = rawId?.trim();
            const extension = file.name.split(".").pop().toLowerCase();

            let error = "";
            if (!memberId) {
                error = "Missing member ID in filename";
            } else if (!/^\d+$/.test(memberId)) {
                error = "Member ID must be numeric";
            } else if (!ACCEPTED_EXTENSIONS.includes(extension)) {
                error = `Only image files are allowed (${ACCEPTED_EXTENSIONS.join(", ")})`;
            }

            return {
                file,
                name: file.name,
                size: formatBytes(file.size),
                memberId,
                isValid: error === "",
                error,
            };
        });
    }, [files]);

    const validFiles = useMemo(
        () => enhancedFiles.filter((item) => item.isValid),
        [enhancedFiles]
    );

    const displayedSelectedFiles = useMemo(
        () => enhancedFiles.slice(0, selectedVisibleCount),
        [enhancedFiles, selectedVisibleCount]
    );

    const displayedSummaries = useMemo(() => {
        return [...uploadSummaries].reverse().slice(0, summaryVisibleCount);
    }, [uploadSummaries, summaryVisibleCount]);

    const handleLazyScroll = useCallback((event, totalItems, setter) => {
        const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
        if (scrollTop + clientHeight >= scrollHeight - 64) {
            setter((prev) => Math.min(prev + 100, totalItems));
        }
    }, []);

    const handleFileSelection = (event) => {
        const selected = Array.from(event.target.files || []);
        if (selected.length === 0) return;

        setFiles((prev) => {
            const existingNames = new Set(prev.map((file) => file.name));
            const deduped = selected.filter((file) => !existingNames.has(file.name));
            return [...prev, ...deduped];
        });
        event.target.value = null;
    };

    const removeFile = (name) => {
        setFiles((prev) => prev.filter((file) => file.name !== name));
    };

    const handleUpload = async () => {
        setErrorMessage("");
        setApiResult(null);
        if (validFiles.length === 0) {
            setErrorMessage("Please add at least one valid image named with the member ID.");
            return;
        }

        const chunks = [];
        for (let i = 0; i < validFiles.length; i += UPLOAD_CHUNK_SIZE) {
            chunks.push(validFiles.slice(i, i + UPLOAD_CHUNK_SIZE));
        }

        setUploadSummaries([]);
        setSummaryVisibleCount(20);

        const aggregatedResult = { uploaded: [], failed: [] };
        const uploadedIds = new Set();

        try {
            setUploading(true);
            setChunkProgress({ current: 0, total: chunks.length });

            for (let index = 0; index < chunks.length; index += 1) {
                const chunk = chunks[index];
                const formData = new FormData();
                chunk.forEach(({ file }) => {
                    formData.append("photos", file, file.name);
                });

                try {
                    const response = await CustomAxios.post("oasis/bulk_photoupload/", formData, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    const chunkUploaded = response.data?.uploaded || [];
                    const chunkFailed = response.data?.failed || [];

                    aggregatedResult.uploaded.push(...chunkUploaded);
                    aggregatedResult.failed.push(...chunkFailed);
                    chunkUploaded.forEach((id) => uploadedIds.add(String(id).trim()));

                    appendSummaryEntry(chunkUploaded, chunkFailed);

                    if (response?.data?.message && chunkFailed.length === 0) {
                        setErrorMessage(response.data.message);
                    }
                } catch (chunkError) {
                    const message =
                        chunkError?.response?.data?.error ||
                        chunkError?.message ||
                        "Bulk upload failed for this batch. Please try again.";
                    const failedEntries = chunk.map((item) => ({
                        file: item.name,
                        error: message,
                    }));
                    aggregatedResult.failed.push(...failedEntries);
                    appendSummaryEntry([], failedEntries);
                }

                setChunkProgress({ current: index + 1, total: chunks.length });
            }

            const resultType =
                aggregatedResult.failed.length === 0
                    ? "success"
                    : aggregatedResult.uploaded.length > 0
                        ? "partial"
                        : "error";

            setApiResult({
                type: resultType,
                data: aggregatedResult,
            });

            if (uploadedIds.size > 0) {
                setFiles((prev) =>
                    prev.filter((file) => !uploadedIds.has(file.name.split(".")[0]?.trim()))
                );
            }
        } catch (error) {
            const message =
                error?.response?.data?.error ||
                error?.message ||
                "Bulk upload failed. Please try again.";
            setApiResult({ type: "error", data: { message } });
        } finally {
            setUploading(false);
            setChunkProgress({ current: 0, total: 0 });
        }
    };

    const clearAll = () => {
        setFiles([]);
        setApiResult(null);
        setErrorMessage("");
    };

    const uploadedCount = apiResult?.data?.uploaded?.length || 0;
    const failedCount = apiResult?.data?.failed?.length || 0;

    return (
        <Box
            sx={{
                width: "100%",
                minHeight: "100vh",
                bgcolor: "#F1F5F9",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}
        >
            <Box sx={{ flex: 1, pt: "60px", px: 3, pb: 3, overflow: "hidden" }}>
                <Box
                sx={{
                    mx: "auto",
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        md: "minmax(0, 2fr) 320px",
                        lg: "minmax(0, 3fr) 360px",
                    },
                    alignItems: "flex-start",
                    gap: 3,
                    height: "100%",
                    overflow: "hidden",
                }}
            >
                <Stack
                    spacing={3}
                    sx={{
                        gridColumn: { xs: "1 / -1", md: "1 / 2" },
                        minWidth: 0,
                        height: "100%",
                        overflowY: "auto",
                        pr: { md: 1 },
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <CloudUploadOutlinedIcon sx={{ fontSize: 32, color: "#288d2c" }} />
                            <Box>
                                <Typography sx={{ fontFamily: "inter", fontSize: 24, fontWeight: 600 }}>
                                    Bulk Photo Upload
                                </Typography>
                                <Typography sx={{ color: "#475569", fontSize: 14 }}>
                                    Upload multiple member photos named as <strong>MEMBERID.ext</strong>
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ display: "flex", gap: 2 }}>
                            <Button variant="outlined" onClick={clearAll} disabled={files.length === 0}>
                                Clear
                            </Button>
                            <Button variant="contained" onClick={handleUpload} disabled={uploading || validFiles.length === 0}>
                                {uploading ? "Uploading..." : `Upload ${validFiles.length || ""}`}
                            </Button>
                        </Box>
                    </Box>

                    <Paper
                        variant="outlined"
                        sx={{
                            borderStyle: "dashed",
                            borderWidth: 2,
                            borderColor: "#CBD5E1",
                            p: 4,
                            textAlign: "center",
                            bgcolor: "#FFFFFF",
                        }}
                        onDragOver={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                        }}
                        onDrop={(event) => {
                            event.preventDefault();
                            handleFileSelection({ target: { files: event.dataTransfer.files } });
                        }}
                    >
                        <input
                            type="file"
                            accept="image/*"
                            multiple
                            hidden
                            ref={fileInputRef}
                            onChange={handleFileSelection}
                        />
                        <Stack spacing={2} alignItems="center">
                            <CloudUploadOutlinedIcon sx={{ fontSize: 48, color: "#94A3B8" }} />
                            <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
                                Drag & drop image files here or
                                <Button onClick={() => fileInputRef.current?.click()} variant="text">
                                    browse
                                </Button>
                            </Typography>
                            <Typography sx={{ color: "#64748B", fontSize: 14 }}>
                                Accepts common image formats (JPG, PNG, WebP, AVIF, TIFF, HEIC). File name must be the numeric member ID.
                            </Typography>
                        </Stack>
                    </Paper>

                    {errorMessage && (
                        <Alert severity="info" icon={<InfoOutlinedIcon />}>
                            {errorMessage}
                        </Alert>
                    )}

                    {uploading && (
                        <Stack spacing={1}>
                            <LinearProgress />
                            {chunkProgress.total > 1 && (
                                <Typography sx={{ fontSize: 13, color: "#475569", textAlign: "right" }}>
                                    Uploading batch {Math.min(chunkProgress.current + 1, chunkProgress.total)} of {chunkProgress.total}
                                </Typography>
                            )}
                        </Stack>
                    )}

                    {apiResult && (
                        <Alert
                            severity={
                                apiResult.type === "success"
                                    ? "success"
                                    : apiResult.type === "partial"
                                        ? "warning"
                                        : "error"
                            }
                        >
                            {Array.isArray(apiResult.data?.uploaded) || Array.isArray(apiResult.data?.failed)
                                ? `${apiResult.data?.uploaded?.length || 0} uploaded · ${apiResult.data?.failed?.length || 0} failed`
                                : apiResult.data?.message || "Upload finished."}
                        </Alert>
                    )}

                    <Paper sx={{ p: 3 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography sx={{ fontSize: 18, fontWeight: 600 }}>Selected Files</Typography>
                            <Chip label={`${files.length} total`} color="default" />
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        {files.length === 0 ? (
                            <Typography sx={{ color: "#94A3B8" }}>No files selected yet.</Typography>
                        ) : (
                            <List
                                sx={{
                                    maxHeight: "360px",
                                    overflowY: "auto",
                                    "&::-webkit-scrollbar": { width: "6px" },
                                    "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: "#CBD5E1",
                                        borderRadius: "999px",
                                    },
                                    "&::-webkit-scrollbar-track": {
                                        backgroundColor: "#F8FAFC",
                                    },
                                }}
                                onScroll={(event) =>
                                    handleLazyScroll(event, enhancedFiles.length, setSelectedVisibleCount)
                                }
                            >
                                {displayedSelectedFiles.map((item) => (
                                    <ListItem
                                        key={item.name}
                                        secondaryAction={
                                            <IconButton edge="end" onClick={() => removeFile(item.name)}>
                                                <DeleteOutlineIcon />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemIconWrapper valid={item.isValid}>
                                            {item.isValid ? (
                                                <CheckCircleOutlineIcon fontSize="small" />
                                            ) : (
                                                <ErrorOutlineIcon fontSize="small" />
                                            )}
                                        </ListItemIconWrapper>
                                        <ListItemText
                                            primary={
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Typography sx={{ fontWeight: 600 }}>{item.name}</Typography>
                                                    <Chip
                                                        label={`ID: ${item.memberId || "—"}`}
                                                        size="small"
                                                        color={item.isValid ? "success" : "warning"}
                                                    />
                                                </Stack>
                                            }
                                            secondary={
                                                item.isValid ? (
                                                    <Typography component="span" sx={{ color: "#64748B" }}>
                                                        {item.size}
                                                    </Typography>
                                                ) : (
                                                    <Typography component="span" sx={{ color: "#DC2626" }}>
                                                        {item.error}
                                                    </Typography>
                                                )
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        )}
                    </Paper>
                </Stack>

                <Stack
                    spacing={3}
                    sx={{
                        gridColumn: { xs: "1 / -1", md: "2 / 3" },
                        minWidth: 0,
                        height: "100%",
                        overflow: "hidden",
                    }}
                >
                    {uploadSummaries.length > 0 ? (
                        <Paper sx={{ p: 3, borderLeft: "4px solid #0F172A", height: "100%", display: "flex", flexDirection: "column" }}>
                            <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 2 }}>
                                Upload Responses
                            </Typography>
                            <Box
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflowY: "auto",
                                    "&::-webkit-scrollbar": { width: "6px" },
                                    "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: "#CBD5E1",
                                        borderRadius: "999px",
                                    },
                                    "&::-webkit-scrollbar-track": {
                                        backgroundColor: "#F8FAFC",
                                    },
                                }}
                                onScroll={(event) =>
                                    handleLazyScroll(event, uploadSummaries.length, setSummaryVisibleCount)
                                }
                            >
                                {displayedSummaries.map((summary, index) => {
                                    const responseNumber = uploadSummaries.length - index;
                                    return (
                                        <Accordion
                                            key={summary.id}
                                            disableGutters
                                            sx={{
                                                mb: 1,
                                                border: "1px solid #E2E8F0",
                                                borderRadius: "8px",
                                                "&:before": { display: "none" },
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                sx={{ bgcolor: "#F8FAFC", borderRadius: "8px" }}
                                            >
                                                <Stack spacing={1} sx={{ width: "100%" }}>
                                                    <Stack
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                    >
                                                        <Typography sx={{ fontWeight: 600 }}>
                                                            Response #{responseNumber}
                                                        </Typography>
                                                        <Chip
                                                            label={summary.status.toUpperCase()}
                                                            size="small"
                                                            color={
                                                                summary.status === "success"
                                                                    ? "success"
                                                                    : summary.status === "partial"
                                                                        ? "warning"
                                                                        : "error"
                                                            }
                                                        />
                                                    </Stack>
                                                    <Typography sx={{ color: "#475569", fontSize: 12 }}>
                                                        {new Date(summary.timestamp).toLocaleString()}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 14 }}>
                                                        {summary.uploadedCount} uploaded · {summary.failedCount} failed
                                                    </Typography>
                                                </Stack>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ bgcolor: "#FFFFFF" }}>
                                                {summary.uploaded.length > 0 && (
                                                    <>
                                                        <Typography sx={{ fontWeight: 600, mb: 1 }}>
                                                            Uploaded ({summary.uploaded.length})
                                                        </Typography>
                                                        <List dense>
                                                            {summary.uploaded.map((id) => (
                                                                <ListItem key={`${summary.id}-uploaded-${id}`}>
                                                                    <ListItemText
                                                                        primary={`Member ID: ${id}`}
                                                                        primaryTypographyProps={{ fontWeight: 500 }}
                                                                    />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    </>
                                                )}
                                                {summary.failed.length > 0 && (
                                                    <>
                                                        <Typography sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                                                            Failed ({summary.failed.length})
                                                        </Typography>
                                                        <List dense>
                                                            {summary.failed.map((item, idx) => (
                                                                <ListItem key={`${summary.id}-failed-${idx}`}>
                                                                    <ListItemText
                                                                        primary={item.file || "Unknown file"}
                                                                        secondary={item.error}
                                                                        primaryTypographyProps={{ fontWeight: 500 }}
                                                                        secondaryTypographyProps={{ color: "#991B1B" }}
                                                                    />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    </>
                                                )}
                                                {summary.uploaded.length === 0 &&
                                                    summary.failed.length === 0 && (
                                                        <Typography sx={{ color: "#94A3B8" }}>
                                                            No file-level details returned for this response.
                                                        </Typography>
                                                    )}
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </Box>
                        </Paper>
                    ) : (
                        <Paper sx={{ p: 3, bgcolor: "#F8FAFC", border: "1px dashed #CBD5E1" }}>
                            <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>
                                Upload responses
                            </Typography>
                            <Typography sx={{ color: "#64748B" }}>
                                Start an upload to see per-response summaries here.
                            </Typography>
                        </Paper>
                    )}
                </Stack>
                </Box>
            </Box>
        </Box>
    );
};

const ListItemIconWrapper = ({ children, valid }) => (
    <Box
        sx={{
            mr: 2,
            display: "flex",
            alignItems: "center",
            color: valid ? "#16A34A" : "#DC2626",
        }}
    >
        {children}
    </Box>
);

export default BulkPhotoUpload;

